import { CalendarIcon } from 'lucide-react';
import { LogoLoader } from '@gokrew/ui';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import EmptyState from '@/components/TableEmptyState';
import { formatDate, getWeekDay } from '@gokrew/ui/utility/utility';
import { useUpcomingHolidays } from '@/hooks/dashboard';
import type { HolidayItem } from '@/types/dashboard';

/**
 * Holidays component is responsible for rendering a list of holidays
 * that are available to the company.
 *
 * @returns Holidays component
 */
export function Holidays() {
  const { t, i18n } = useTranslation(TranslationNS.LeaveManagement);
  const currentYear = new Date().getFullYear();
  const { data, isLoading } = useUpcomingHolidays({
    page: 1,
    limit: 99,
    year: currentYear,
  });

  const holidays: HolidayItem[] = data?.data?.data || [];
  if (isLoading) return <LogoLoader size="sm" className="mx-auto py-6" />;

  if (!holidays.length)
    return (
      <EmptyState
        Icon={CalendarIcon}
        title={t('holidays_dialog.empty_state.title')}
        message={t('holidays_dialog.empty_state.message')}
        className="mt-10"
      />
    );

  return (
    <div className="px-1 sm:px-2">
      <ul className="list-disc-styled">
        {holidays.map((holiday) => (
          <li key={holiday.id} className="list-item-text">
            <span className="font-semibold text-gray-900 capitalize wrap-break-word">{holiday.name}:</span>{' '}
            {formatDate(holiday.leaveDate, {
              format: 'D MMMM YYYY',
              locale: i18n.language,
            })}
            <span className="text-subheading ml-1">
              ({getWeekDay(holiday.leaveDate, { weekday: 'long' }, i18n.language)})
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
